import {
  Box,
  CircularProgress,
  Grid,
  Grow,
  IconButton,
  List,
  MenuItem,
  Button as MuiButton,
  Popper,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import { ListRange, Virtuoso } from 'react-virtuoso';
import {
  extractPlainText,
  getFormattedDate,
  getPageScrollInfo,
  handlePageScrollPosition,
} from 'utility/helpers';
import HandleOperation from 'components/utils/HandleOperation';
import PaginationLoader from 'components/utils/PaginationLoader';
import { styled } from '@mui/material/styles';
import ArticleSkelton from 'pages/project/components/ArticleSkelton';
import ArticleBox, { AvatarContainer } from '@buildwise/pages/knowledge/components/ArticleBox';
import NoFeed from '@buildwise/pages/knowledge/components/NoFeed';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import useAllPosts from '../hooks/useAllPosts';
import AllPostsFilters from './AllPostsFilters';
import useUserData from 'hooks/useUserData';
import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import ArticlePrintPreview from '@buildwise/pages/knowledge/components/ArticlePrintPreview';
import Dot from 'components/common/dot/Dot';
import moment from 'moment';
import Upload2LineIcon from 'remixicon-react/Upload2LineIcon';
import DownloadIcon from 'remixicon-react/Download2LineIcon';
import LightbulbFlashFillIcon from 'remixicon-react/LightbulbFlashFillIcon';
import Avatar from 'components/common/avatar/Avatar';
import CloseIcon from 'remixicon-react/CloseCircleFillIcon';
import { useTheme } from '@mui/material/styles';
import { exportToCSV } from 'utility/helpers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CONFIGS, getFieldDefaultValue, useConfigQuery } from '@buildwise/libs/configurations';
import usePostsFiltersStore from 'store/usePostsFiltersStore';
import { FeedFilter } from 'api/KnowledgeAPI';
import { ConfigEntryFieldType } from 'config/config';

dayjs.extend(utc);
dayjs.extend(timezone);

type AllPostsProps = {
  inlineFilters?: boolean;
  scrollInfoKey?: string;
};

const AllPosts = ({ scrollInfoKey }: AllPostsProps) => {
  const theme = useTheme();
  const { isNovaAdmin, companyId, company, user } = useUserData();
  const {
    activeUniqueEntries,
    isFetched: isFetchedFilters,
    isLoading: isLoadingFilters,
  } = useConfigQuery({
    companyId,
    configName: CONFIGS.COMPANY_FILTERS.configName,
  });
  const {
    filters,
    userFiltersSynced,
    resetFilters: resetAllFilters,
    setFilters: setAllFilters,
  } = usePostsFiltersStore();
  const { [companyId]: allFilters = [] } = filters;
  const userFiltersValues = useMemo(
    () =>
      activeUniqueEntries.map((filter) => ({
        filterName: filter.entryCode,
        filterValues:
          user?.userFilters?.[filter.entryCode] || getFieldDefaultValue({ field: filter }),
        ...(filter.referenceObject?.type === ConfigEntryFieldType.DATE &&
          filter.entryCode === 'createdAt' && {
            [`MIN_${filter.entryCode}`]:
              user?.userFilters?.[`MIN_${filter.entryCode}`]?.[0] || null,
            [`MAX_${filter.entryCode}`]:
              user?.userFilters?.[`MAX_${filter.entryCode}`]?.[0] || null,
            duration: user?.userFilters?.duration?.[0] || null,
          }),
      })),
    [user?.userFilters, activeUniqueEntries],
  );
  const selectedFilters: FeedFilter[] = useMemo(
    () =>
      allFilters.filter((filter) =>
        filter.filterValues && Array.isArray(filter.filterValues)
          ? filter.filterValues.length > 0
          : filter.filterValues || filter.startValue || filter.endValue,
      ),
    [allFilters],
  );
  const {
    userMap,
    articles,
    containerRef,
    enableRefresh,
    isFetching,
    isFetchingNextPage,
    isIdle,
    isLoading,
    isRefetching,
    virtuosoRef,
    fetchPosts,
    resetFeed,
    refetchQuery,
    setEnableRefresh,
  } = useAllPosts({
    filters: selectedFilters,
    shouldFetch: isFetchedFilters && userFiltersSynced,
  });
  const [showCheckbox, setShowCheckbox] = useState<boolean>(false);
  const [articlePreviewInfo, setArticlePreviewInfo] = useState<ArticleType[]>([]);
  const articleInfoRef = useRef<HTMLDivElement>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'popper' : undefined;

  useEffect(() => {
    if (!userFiltersSynced && isFetchedFilters) {
      setAllFilters(companyId, userFiltersValues, true);
    }
  }, [userFiltersValues, companyId, userFiltersSynced, isFetchedFilters]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleResetSelectedFiles = () => {
    setShowCheckbox(false);
    setArticlePreviewInfo([]);
    setIsPreviewLoading(false);
  };

  const handlePrint = useReactToPrint({
    content: () => articleInfoRef.current,
    onBeforeGetContent: () => {
      setIsPreviewLoading(true); // Set isPreviewLoading to true before printing
    },
    onAfterPrint: () => {
      setIsPreviewLoading(false); // Reset isPreviewLoading after printing
    },
    documentTitle: `BuildWise Export`,
    pageStyle: `
    body {
      -webkit-print-color-adjust: exact; /* Chrome, Safari */
      print-color-adjust: exact;         /* Firefox */
    }
    .page-break {
      page-break-before: always;
    }
    .header {
      position: fixed;
      height : 80px;
      top : 0;
      left: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .footer {
      position: fixed;
      height : 80px;
      bottom: 0;
      left: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: white;
      margin-top: 20px;
    } 
  `,
  });

  const handleExport = (articles: ArticleType[]) => {
    const userTimeZone = dayjs.tz.guess();
    let headers = ['title', 'description', 'createdBy', 'divisions', 'topics', 'specSections'];
    const jsonData: any = [];
    articles.forEach((article) => {
      let data = {};
      data['job title'] = article?.userNameTitleToDisplay;
      data['username'] = article?.userNameToDisplay;
      data['published date'] = dayjs(article?.publishedAt).format(
        `${userTimeZone === 'Asia/Calcutta' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}`,
      );
      headers.forEach((_, index) => {
        let value = article[headers[index]];
        data[headers[index]] = Array.isArray(value)
          ? value?.join(',') || ''
          : headers[index] === 'description'
            ? `${extractPlainText(value)}`
            : value;
      });

      // appending attachments
      if (article?.attachments?.length) {
        if (!headers.includes('attachments')) headers.push('attachments');
        const fileNames = article.attachments.map((attachment) => attachment.fileName).join(',');
        data['attachments'] = fileNames;
      } else {
        data['attachments'] = '';
      }

      jsonData.push(data);
    });
    headers = [
      'title',
      'description',
      'createdBy',
      'username',
      'job title',
      'published date',
      'divisions',
      'topics',
      'specSections',
    ];
    const filename = `BuildWise-Export_${getFormattedDate()}.csv`;
    exportToCSV(jsonData, headers, filename);
    handleResetSelectedFiles();
  };

  const RenderPdfHeader = useMemo(() => {
    return (
      <Box className="header">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '85%',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {company?.companyLogoDisplay &&
            company?.companyLogoDisplay !== 'NAME' &&
            company?.companyLogoURL ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                {(!company?.companyLogoDisplay ||
                  ['BOTH', 'LOGO'].includes(company?.companyLogoDisplay)) && (
                  <Box
                    component="img"
                    src={company?.companyLogoURL}
                    maxWidth={200}
                    maxHeight={40}
                  />
                )}

                {company?.companyLogoDisplay === 'BOTH' && (
                  <Box sx={{ marginLeft: '5px' }}>
                    <$BoldText>{company?.name}</$BoldText>
                  </Box>
                )}
              </Box>
            ) : (
              <$BoldText>{company?.name}</$BoldText>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              sx={{
                mr: 1.5,
                mt: -2,
              }}
            >
              <AvatarContainer>
                <Avatar
                  src={user?.profileImageURL}
                  size={38}
                  icon={<LightbulbFlashFillIcon color="#FFC107" />}
                />
              </AvatarContainer>
            </Box>
            <Box display="flex" flexDirection="column" mb={'-4px'}>
              <$User title={user?.userNameToDisplay} sx={{ lineHeight: 1.2, fontWeight: 'blod' }}>
                {user?.userNameToDisplay}
              </$User>
              {user?.title && (
                <Typography variant="caption" title="Job Title">
                  {user.title}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }, [company, user]);

  const clearFilters = useCallback(() => {
    resetAllFilters(companyId);
  }, [companyId]);

  const setFilters = (companyId, filters, tab, userFiltersSynced) => {
    return setAllFilters(companyId, filters, userFiltersSynced);
  };

  return (
    <Grid container spacing={2} minHeight="100vh">
      <Grid item xs={12} md={12}>
        <StickyContainer id="posts-filter-container" justifyContent="flex-end">
          {isNovaAdmin && (
            <IconButton
              onClick={() => {
                setShowCheckbox((prevState) => !prevState);
                setArticlePreviewInfo([]);
                setIsPreviewLoading(false);
              }}
              sx={{
                mr: 1,
                backgroundColor: showCheckbox ? 'rgba(80, 77, 100, 0.12)' : 'unset',
              }}
              size="small"
              title="Export"
              disabled={isPreviewLoading}
            >
              <Upload2LineIcon size={22} color={showCheckbox ? '#091E42' : '#525659'} />
            </IconButton>
          )}
          {activeUniqueEntries.length > 0 && (
            <AllPostsFilters
              selectedFilters={selectedFilters}
              clearFilters={clearFilters}
              filters={filters}
              setFilters={setFilters}
            />
          )}
        </StickyContainer>
        <Box ref={containerRef} visibility="hidden" mt={activeUniqueEntries.length > 0 ? 0 : 1}>
          <Grow
            in={isFetching && !isFetchingNextPage && enableRefresh}
            style={{ transformOrigin: 'top center' }}
            timeout={500}
          >
            <Box position="relative">
              <AbsoluteContainer>
                <MuiButton
                  variant="contained"
                  size="small"
                  sx={(theme) => ({
                    margin: '0 auto',
                    backgroundColor: theme.palette.custom.yellowBorder,
                  })}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Refreshing...
                </MuiButton>
              </AbsoluteContainer>
            </Box>
          </Grow>

          <HandleOperation
            isLoading={
              isLoading ||
              (isFetching && !isFetchingNextPage && !enableRefresh) ||
              isIdle ||
              isLoadingFilters
            }
            data={articles}
            skelton={<ArticleSkelton variant="short" />}
            skeltonCount={5}
            noDataComponent={
              <NoFeed
                tab="posts"
                clearFilters={clearFilters}
                hasActiveFilters={selectedFilters.length > 0}
              />
            }
          >
            <Box pb={5}>
              <Virtuoso
                rangeChanged={(range: ListRange) => {
                  if (virtuosoRef.current && containerRef.current?.style.visibility !== 'hidden') {
                    handlePageScrollPosition(
                      scrollInfoKey || 'posts',
                      0,
                      window.scrollY === 0
                        ? 0
                        : Math.floor((range.startIndex + range.endIndex) / 2),
                    );
                  }
                }}
                ref={virtuosoRef}
                useWindowScroll
                data={articles}
                endReached={fetchPosts}
                atTopStateChange={(atTop) => {
                  const scrollToIndex = getPageScrollInfo<ArticleType>(
                    scrollInfoKey || 'posts',
                    articles,
                    'id',
                  );
                  if (!atTop) {
                    setEnableRefresh(true);
                  }

                  if (
                    scrollToIndex <= 2 &&
                    atTop &&
                    !isLoading &&
                    !isFetching &&
                    !isFetchingNextPage &&
                    !isRefetching &&
                    articles.length > 0 &&
                    enableRefresh
                  ) {
                    refetchQuery({ type: 'posts' });
                  }
                }}
                itemContent={(index, item) => (
                  <ArticleBox
                    key={item.id}
                    article={item}
                    articleIndex={index}
                    variant="short"
                    doNotAutoplayVideos
                    scrollInfoKey={scrollInfoKey || 'posts'}
                    resetFeed={resetFeed}
                    selectedFeedFilters={selectedFilters}
                    showCheckbox={showCheckbox}
                    articlePreviewInfo={articlePreviewInfo}
                    setArticlePreviewInfo={setArticlePreviewInfo}
                    isPreviewLoading={isPreviewLoading}
                    isSME={!!userMap[item?.createdBy]?.[0]?.isSME}
                  />
                )}
                components={{
                  Footer: () => <PaginationLoader isLoading={isFetchingNextPage} />,
                }}
              />
            </Box>
          </HandleOperation>
        </Box>
      </Grid>

      {articlePreviewInfo?.length ? (
        <Box sx={{ display: 'none' }}>
          <Box
            ref={articleInfoRef}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: '85%' }}>
              {RenderPdfHeader}
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <td style={{ height: '80px' }}></td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {articlePreviewInfo.map((item, index) => (
                        <ArticlePrintPreview
                          key={item.id}
                          article={item}
                          articleIndex={index}
                          variant="detailed"
                          doNotAutoplayVideos
                          scrollInfoKey={scrollInfoKey || 'posts'}
                          resetFeed={resetFeed}
                          selectedFeedFilters={selectedFilters}
                          showCheckbox={showCheckbox}
                          exportedArticles={articlePreviewInfo}
                          setArticlePreviewInfo={setArticlePreviewInfo}
                        />
                      ))}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ height: '70px' }}></td>
                  </tr>
                </tfoot>
              </table>
              <Box className="footer">
                <PdfFooter>
                  <$CompanyLogo src="https://images.buildwise.ai/BuildWise_logo_without_name.png" />
                  <Typography component="strong" fontWeight={700} variant="subtitle2">
                    Powered by BuildWise.AI
                  </Typography>
                  <Dot />
                  <Typography variant="subtitle2">
                    Exported on {moment().format('MMM DD, YYYY')}
                  </Typography>
                </PdfFooter>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      {showCheckbox ? (
        <StickyFooter>
          <StickyFooterContentContainer>
            <Box sx={{ ml: 4, display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleResetSelectedFiles} disabled={isPreviewLoading}>
                <CloseIcon size={24} color={theme.palette.custom.white} />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={(theme) => ({ color: theme.palette.custom.white })}
              >
                {articlePreviewInfo?.length > 1
                  ? `${articlePreviewInfo?.length} Posts Selected`
                  : articlePreviewInfo?.length === 1
                    ? '1 Post Selected'
                    : 'No Post Selected'}
              </Typography>
              <MuiButton
                variant="text"
                size="medium"
                sx={(theme) => ({
                  color: theme.palette.custom.white,
                  fontWeight: 700,
                  cursor: 'pointer',
                })}
                onClick={() => {
                  if (articles.length === articlePreviewInfo.length) {
                    setArticlePreviewInfo([]);
                  } else {
                    setArticlePreviewInfo([...articles]);
                  }
                }}
                disabled={isPreviewLoading}
              >
                {articles.length === articlePreviewInfo.length ? 'Deselect All' : 'Select All'}
              </MuiButton>
            </Box>
            <Box>
              {/* <MuiButton
                variant="text"
                size="medium"
                sx={{ mr: 2 }}
                onClick={() => {
                  setArticlePreviewInfo([]);
                  setShowCheckbox(false);
                  setIsPreviewLoading(false);
                }}
              >
                Cancel
              </MuiButton>
              <MuiButton
                variant="contained"
                size="medium"
                sx={{ mr: 2 }}
                onClick={() => {
                  handlePrint();
                }}
                disabled={isPreviewLoading}
              >
                {isPreviewLoading ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Preview'
                )}
              </MuiButton> */}
              {!!articlePreviewInfo.length && (
                <ClickAwayListener
                  onClickAway={() => {
                    setAnchorEl(null);
                  }}
                >
                  {isPreviewLoading ? (
                    <MuiButton
                      variant="contained"
                      size="medium"
                      sx={{ mr: 2, backgroundColor: theme.palette.custom.jetGrey }}
                      disabled={isPreviewLoading}
                    >
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.white })}
                        size={20}
                      />
                    </MuiButton>
                  ) : (
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        color: theme.palette.custom.white,
                        alignItems: 'center',
                        cursor: 'pointer',
                      })}
                      onClick={handleClick}
                    >
                      <DownloadIcon size={24} />
                      <Typography sx={{ marginLeft: 1 }}>Export As...</Typography>
                    </Box>
                  )}
                </ClickAwayListener>
              )}
            </Box>
            <Popper
              id={id}
              open={open}
              anchorEl={anchorEl}
              placement="top-start"
              sx={{
                zIndex: 1000,
                bgcolor: 'background.paper',
                border: '1px solid #D9DBE9',
                borderRadius: '8px',
                marginTop: '10px !important',
              }}
            >
              <Box
                sx={{
                  borderRadius: '8px',
                  paddingTop: '2px',
                }}
              >
                <List>
                  {['Export as PDF', 'Export as CSV'].map((option, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (index === 0) {
                          handlePrint();
                        } else {
                          handleExport(articlePreviewInfo);
                        }
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </List>
              </Box>
            </Popper>
          </StickyFooterContentContainer>
        </StickyFooter>
      ) : null}
    </Grid>
  );
};

const AbsoluteContainer = styled(Box)({
  position: 'absolute',
  top: 20,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
});

const StickyContainer = styled(Box, { label: 'StickyContainer' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  position: 'sticky',
  top: 140,
  paddingTop: 8,
  paddingBottom: 16,
  backgroundColor: theme.palette.custom.background,
  zIndex: 2,
  width: '100%',
}));

const StickyFooter = styled(Box, { label: 'StickyFooter' })(({ theme }) => ({
  margin: 'auto',
  position: 'fixed',
  bottom: 0,
  left: 0,
  Padding: '0px 8px',
  backgroundColor: theme.palette.custom.tuna,
  zIndex: 2,
  width: '100%',
  height: '60px',
}));

const StickyFooterContentContainer = styled(Box, { label: 'StickyFooterContentContainer' })(
  ({ theme }) => ({
    maxWidth: theme.maxWidth,
    border: '1ps solid red',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 auto',
    padding: '0 48px',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    positon: 'relative',
  }),
);

const $CompanyLogo = styled('img', { label: 'Image' })({
  height: '35px',
  marginRight: '8px',
});

const PdfFooter = styled(Box, { label: 'PdfFooter' })({
  display: 'flex',
  borderTop: '1px solid #ededf1',
  width: '85%',
  alignItems: 'center',
  padding: '10px 0',
});

const $User = styled(Typography, { label: 'User' })({
  fontWeight: 'bold',
  fontSize: '16px',
});

const $BoldText = styled(Typography, { label: 'Typography' })<{ fontSize?: number }>(
  ({ theme, fontSize = 24 }) => ({
    fontSize,
    fontWeight: 700,
    textTransform: 'capitalize',
    color: theme.palette.custom.body,
    lineHeight: '27px',
  }),
);

export default AllPosts;
